import React, { useEffect, useState } from "react";
import { useOutletContext, Link, useSearchParams } from "react-router-dom";
import { api, errMsg, cleanPhone } from "@/lib/auth";
import { PageHeader } from "@/pages/Classes";
import { toast } from "sonner";
import { sanitizeHtml } from "@/lib/sanitize";
import { Trophy } from "lucide-react";
import { fmtRange, crewDays } from "@/lib/enrol";
import EmailOptInCheckbox from "@/components/EmailOptInCheckbox";

export default function CompetitionTeam() {
  const { settings } = useOutletContext();
  const [crews, setCrews] = useState([]);
  const [ach, setAch] = useState([]);
  const [sp] = useSearchParams();
  const [open, setOpen] = useState(sp.get("apply") === "1");
  const [form, setForm] = useState({ crew_id: sp.get("crew") || "", email_opt_in: false, full_name: "", age: "", email: "", phone: "", dance_experience: "", dance_styles: "", previous_competition: "", current_training: "", why_join: "", notes: "" });

  useEffect(() => {
    api.get("/crews").then(r => setCrews(r.data));
    api.get("/achievements").then(r => setAch(r.data));
  }, []);

  const submit = async () => {
    if (!form.crew_id) return toast.error("Please choose which crew you're applying for");
    try {
      await api.post("/competition-applications", { ...form, age: parseInt(form.age) });
      toast.success("Application received! We'll be in touch about the next audition.");
      setOpen(false); setForm({ full_name: "", age: "", email: "", phone: "", dance_experience: "", dance_styles: "", previous_competition: "", current_training: "", why_join: "", notes: "" });
    } catch (e) { toast.error(errMsg(e, "Failed to submit")); }
  };

  return (
    <>
      <PageHeader eyebrow="Selective · Audition only" title="Competition Team" sub="A serious track for dancers ready to perform, compete and grow faster." back />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
        {settings?.competition_intro && (
          <section className="max-w-[900px] mx-auto pt-4 pb-12" data-testid="competition-intro">
            <div className="prose prose-invert prose-lg max-w-none leading-relaxed [&_*]:!text-inherit" style={{ color: settings?.competition_intro_color || "#e4e4e7" }} dangerouslySetInnerHTML={{ __html: sanitizeHtml((settings.competition_intro || "").replace(/color\s*:\s*[^;"']+;?/gi, "").replace(/font-family\s*:\s*[^;"']+;?/gi, "").replace(/background(?:-color)?\s*:\s*[^;"']+;?/gi, "")) }} />
          </section>
        )}

        {crews.length > 0 ? crews.map(c => (
          <section key={c.id} className="mb-20" data-testid={`crew-${c.id}`}>
            <div className="relative aspect-[21/9] overflow-hidden mb-8">
              <img src={c.group_photo || (Array.isArray(c.photos) && c.photos[0] && (typeof c.photos[0] === "string" ? c.photos[0] : c.photos[0].url)) || "https://images.unsplash.com/photo-1621976498727-9e5d56476276?w=2000"} alt={c.name} className="absolute inset-0 w-full h-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"/>
              <div className="absolute bottom-6 left-6">
                <div className="eyebrow">{c.achievements || "Crew"}{c.age_range ? ` · ${c.age_range}` : ""}</div>
                <div className="font-display text-4xl md:text-6xl uppercase">{c.name}</div>
                {crewDays(c) && <div className="text-zinc-300 text-sm mt-2" data-testid={`crew-schedule-${c.id}`}>Trains {crewDays(c)}{c.start_time ? ` · ${fmtRange(c.start_time, c.end_time)}` : ""}{c.location_name ? ` · ${c.location_name}` : ""}</div>}
              </div>
              {(() => {
                const mine = ach.filter(a => a.crew_id === c.id);
                if (!mine.length) return null;
                const isFirst = (t = "") => /\b(1st|first|gold|champion|winner)\b/i.test(t);
                const isSecond = (t = "") => /\b(2nd|second|silver|runner)\b/i.test(t);
                const isThird = (t = "") => /\b(3rd|third|bronze)\b/i.test(t);
                const g = mine.filter(a => isFirst(a.title)).length, sv = mine.filter(a => isSecond(a.title)).length, b = mine.filter(a => isThird(a.title)).length;
                return (
                  <div className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 px-3 py-2" data-testid={`crew-trophies-${c.id}`}>
                    <Trophy size={16} className="text-yellow-400" />
                    <span className="font-display text-2xl leading-none">{mine.length}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-300">accolade{mine.length === 1 ? "" : "s"}</span>
                    {(g || sv || b) ? (
                      <span className="flex items-center gap-1.5 ml-2 pl-2 border-l border-white/10 text-[11px] font-bold tabular-nums">
                        {g > 0 && <span className="text-yellow-400" title="1st places" data-testid={`crew-gold-${c.id}`}>{g}×1st</span>}
                        {sv > 0 && <span className="text-zinc-300" title="2nd places" data-testid={`crew-silver-${c.id}`}>{sv}×2nd</span>}
                        {b > 0 && <span className="text-amber-600" title="3rd places" data-testid={`crew-bronze-${c.id}`}>{b}×3rd</span>}
                      </span>
                    ) : null}
                  </div>
                );
              })()}
            </div>
            {c.description && <p className="text-zinc-300 text-lg mb-6 max-w-2xl">{c.description}</p>}
            {c.crew_intro && (
              <div className="prose prose-invert max-w-none text-zinc-300 mb-8" data-testid={`crew-intro-${c.id}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.crew_intro) }} />
            )}
            {Array.isArray(c.photos) && c.photos.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-8" data-testid={`crew-photos-${c.id}`}>
                {c.photos.map((p, idx) => {
                  const url = typeof p === "string" ? p : p?.url;
                  const caption = typeof p === "string" ? "" : (p?.caption || "");
                  if (!url) return null;
                  return (
                    <figure key={url} className="relative aspect-square overflow-hidden card-hover group" data-testid={`crew-photo-${c.id}-${idx}`}>
                      <img src={url} alt={caption || `${c.name} #${idx+1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                      {caption && (
                        <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black via-black/70 to-transparent text-xs text-white leading-snug" data-testid={`crew-photo-caption-${c.id}-${idx}`}>
                          {caption}
                        </figcaption>
                      )}
                    </figure>
                  );
                })}
              </div>
            )}
            {ach.filter(a => a.crew_id === c.id).length > 0 && (
              <div className="mb-8" data-testid={`crew-accolades-${c.id}`}>
                <div className="eyebrow mb-3">Accolades</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {ach.filter(a => a.crew_id === c.id).map(a => (
                    <div key={a.id} className="card-surface p-4 border-l-2 border-orange-500/60" data-testid={`crew-accolade-${a.id}`}>
                      <div className="text-red-500 font-display text-2xl leading-none">{a.title}</div>
                      <div className="font-semibold text-sm mt-1">{a.event}</div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-widest mt-1">{a.date}</div>
                    </div>
                  ))}
                </div>
                <Link to="/accolades" className="inline-block mt-3 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-red-500">All accolades →</Link>
              </div>
            )}
            {c.members?.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {c.members.map(m => (
                  <div key={m.id} className="card-surface overflow-hidden card-hover">
                    {m.photo && <img src={m.photo} alt={m.name} className="w-full aspect-[3/4] object-cover" />}
                    <div className="p-4">
                      <div className="font-display text-xl uppercase leading-none">{m.name}</div>
                      {m.role && <div className="text-red-500 text-xs uppercase tracking-widest mt-2 font-bold">{m.role}</div>}
                      {m.bio && <p className="text-zinc-400 text-sm mt-3">{m.bio}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )) : (
          <div className="text-center py-20 text-zinc-500">Competition crews will appear here once added by the team.</div>
        )}

        <div className="text-center my-16" data-testid="apply-cta-block">
          <div className="eyebrow mb-4">Ready to level up?</div>
          <button onClick={() => setOpen(true)} className="btn-red" data-testid="apply-competition">Apply / Express interest</button>
        </div>

        {ach.filter(a => !a.crew_id).length > 0 && (
          <>
            <div className="eyebrow mb-4 mt-16">Other competition wins</div>
            <div className="grid md:grid-cols-3 gap-4 mb-16">
              {ach.filter(a => !a.crew_id).map(a => (
                <div key={a.id} className="card-surface p-6">
                  <div className="text-red-500 font-display text-3xl">{a.title}</div>
                  <div className="font-semibold mt-1">{a.event}</div>
                  <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">{a.date}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="card-surface p-8 max-w-lg w-full my-8" onClick={e=>e.stopPropagation()}>
            <h3 className="font-display text-3xl uppercase mb-6">Application</h3>
            <label className="eyebrow block mb-1">Which crew are you applying for? *</label>
            <select value={form.crew_id} onChange={e => setForm({ ...form, crew_id: e.target.value })} className="w-full bg-black border border-white/10 px-3 py-2 mb-3 focus:border-red-600 outline-none" data-testid="comp-division">
              <option value="">— Select —</option>
              {crews.map(c => <option key={c.id} value={c.id}>{c.name}{c.age_range ? ` (${c.age_range})` : ""}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              {[["full_name","Full name"],["age","Age"],["email","Email"],["phone","Phone"],["dance_experience","Dance experience"],["dance_styles","Styles"],["previous_competition","Previous comps"],["current_training","Current training"]].map(([k,l]) => (
                <input key={k} placeholder={l} value={form[k]} onChange={e => setForm({...form,[k]: k === "phone" ? cleanPhone(e.target.value) : e.target.value})} className="bg-black border border-white/10 px-3 py-2 focus:border-red-600 outline-none text-sm" />
              ))}
            </div>
            <textarea placeholder="Why do you want to join?" value={form.why_join} onChange={e => setForm({...form,why_join:e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 mt-3 focus:border-red-600 outline-none text-sm" rows={3} />
            <div className="mt-3"><EmailOptInCheckbox checked={form.email_opt_in} onChange={v => setForm({ ...form, email_opt_in: v })} testId="comp-email-opt-in" /></div>
            <button onClick={submit} disabled={!form.crew_id} className="btn-red w-full mt-4 disabled:opacity-50" data-testid="submit-competition-app">Submit application</button>
          </div>
        </div>
      )}
    </>
  );
}
